import styles from './UrlPatternHelp.module.css';

/**
 * What a URL pattern actually matches, said once and shown everywhere a
 * pattern can be typed.
 *
 * This exists because the settings page used to describe a matcher we do not
 * have. It captioned `https://acme.example.com/*` as "any page on the
 * marketing site", and `*` matches exactly one path segment - so that pattern
 * covered `/pricing` and missed `/blog/hello`. Somebody following the examples
 * got routing that worked on the pages they happened to test and silently
 * failed on the rest, which is worse than no examples at all.
 *
 * The three surfaces that can take a pattern (the settings tab, the new
 * project modal, and the board header) now share this component, so the copy
 * cannot drift back apart.
 */
export function UrlPatternHelp({ heading = 'How patterns match' }: { heading?: string | null }) {
  return (
    <div className={styles.help}>
      {heading ? <strong className={styles.heading}>{heading}</strong> : null}
      <dl className={styles.rows}>
        <div className={styles.row}>
          <dt><code className={styles.code}>https://acme.example.com</code></dt>
          <dd>The whole site. An address on its own covers every page under it.</dd>
        </div>
        <div className={styles.row}>
          <dt><code className={styles.code}>https://acme.example.com/checkout/**</code></dt>
          <dd>
            <code className={styles.code}>**</code> is any depth: the checkout flow and everything under it.
          </dd>
        </div>
        <div className={styles.row}>
          <dt><code className={styles.code}>https://acme.example.com/checkout/*</code></dt>
          <dd>
            <code className={styles.code}>*</code> is exactly one segment: <code className={styles.code}>/checkout/cart</code>,
            but not <code className={styles.code}>/checkout/cart/edit</code>.
          </dd>
        </div>
      </dl>
      <p className={styles.note}>
        The host must match exactly, so a pattern can never reach another site.
        Subdomains are separate: add <code className={styles.code}>https://app.acme.example.com</code> as its own pattern.
      </p>
    </div>
  );
}
